import React from 'react';
import { Card, CardContent, Typography, Chip, Stack, Avatar, Box, IconButton } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IActivityFeedItem } from '../../redux/activity/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { convertMinuteSecondsDays, formatNumberWithK } from '../../utils/AppUtils';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import CustomCommentIcon from '../CustomSVGIcons/CustomCommentIcon';
import LoveIcon from '../CustomSVGIcons/LoveIcon';
import { EsFollowButton } from '../EsButtons/EsFollowButton';
import { EsCalendarTag } from '../EsCards/EsCalendarTag';
import { IContest, UserSummary } from '../../types/global/helper';
import { confetti_I } from '../../assets/animations';
import { useLottie } from 'lottie-react';
import CustomButton from '../Button/CustomButton';
import { Category, LocalOffer } from '@mui/icons-material';
import { isEmpty } from '../../utils/lodash';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import { RichTextViewer } from '../Common/RichTextViewer';
import { useAuth } from '../../hook/useAuth';
import { useDispatch, useSelector } from 'react-redux';
import { toggleAuthModal } from '../../redux/authentication/action';
import { activityLikeActionInProgressSelector } from '../../selectors/selectors';

export interface IFeedProjectCardProps {
    contest: IActivityFeedItem;
    onJoinCampaignClick?: () => void;
    onCommentClick?: VoidFunction;
    onLikeClick?: VoidFunction;
    likingInProgress?: boolean;
}

const FeedContestCard: React.FC<IFeedProjectCardProps> = ({
    onJoinCampaignClick,
    contest,
    onCommentClick,
    onLikeClick,
}) => {
    const { themeStyle } = useThemeContext();
    const { contests_activity, created_by } = contest;
    const isWonContest = contest && contest.activity_type.startsWith('won a contest');
    const likingInProgress = useSelector(activityLikeActionInProgressSelector);
    const { isSignedIn } = useAuth();
    const dispatch = useDispatch();

    const isNewContest =
        (contest && contest.activity_type.startsWith('created a')) ||
        contest.activity_type.startsWith('joined a') ||
        contest.activity_type.startsWith('won a');
    const options = {
        animationData: confetti_I,
        loop: true,
    };
    const style = {
        height: '100%',
        width: '100%',
    };
    const { View } = useLottie(options, style);

    const handleLike = () => {
        if (!isSignedIn) {
            dispatch(toggleAuthModal(true));
            return;
        }

        if (likingInProgress) {
            return;
        }

        onLikeClick && onLikeClick();
    };

    const handleShowSubmissions = () => {
        // Navigate to the contest submissions page
        if (isWonContest) {
            window.location.href = `/campaign/${contest?.contests_activity?.slug}/entries`;
        } else {
            onJoinCampaignClick && onJoinCampaignClick();
        }
    };

    return (
        <Card
            sx={{
                width: '100%',
                borderRadius: 2,
                overflow: 'visible',
                my: 2,
                backgroundColor: theme[themeStyle].secondaryBg,
            }}>
            <CardContent sx={{ px: 0, pb: 0 }}>
                {/* Skill Match Percentage */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                    <Stack direction="row" alignItems="center" gap={'10px'} width={'100%'}>
                        <ThspianAvatar user={contest?.created_by!} enableDefaultNavigation withMonogram />
                        <Stack>
                            {contest && contest?.created_by && (
                                <Stack direction="row" gap={'3px'}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 10,
                                        }}>
                                        {contest?.created_by.firstName}
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: theme[themeStyle].tag_white,
                                            fontSize: 10,
                                        }}>
                                        {contest.activity_type}
                                    </Typography>
                                </Stack>
                            )}
                            <Stack alignItems="flex-start">
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        fontSize: 8,
                                    }}>
                                    {convertMinuteSecondsDays(contest.createdAt)} ago
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>
                    <Stack>
                        <EsFollowButton
                            isFollowing={created_by.user_following}
                            intrinsic
                            user={created_by as unknown as UserSummary}
                            location="feed"
                        />
                    </Stack>
                </Stack>
                {contests_activity?.feature_image && (
                    <Stack
                        sx={{
                            maxHeight: '350px',
                            overflow: 'hidden',
                            width: '100%',
                            my: 2,
                            // Add gradient here
                        }}
                        position="relative">
                        <CustomLazyLoadImage src={contests_activity?.feature_image} />

                        <EsCalendarTag
                            contest={contest.contests_activity as unknown as IContest}
                            sx={{ position: 'absolute', top: 0, left: 0 }}
                        />

                        {isWonContest && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                    flex: 1,
                                    zIndex: 2,
                                }}>
                                {View}
                            </Box>
                        )}
                    </Stack>
                )}
                {contest.contests_activity && contest.contests_activity?.host && (
                    <Stack left={4} bottom={0} alignItems="flex-start" gap={1} px={2}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                marginBottom: 1,
                                color: theme[themeStyle].tag_white,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            {contest.contests_activity?.title}
                        </Typography>

                        <RichTextViewer
                            content={contest.contests_activity?.description}
                            lineClamp={2}
                            textStyle={{ padding: 0, fontSize: 12, fontFamily: FONT_ENUM.MAIN_MEDIUM }}
                            theme={themeStyle}
                        />
                    </Stack>
                )}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }} px={2} my={2}>
                    <LocalOffer
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontSize: '18px',
                        }}
                        style={{
                            transform: 'scaleX(-1)',
                        }}
                    />
                    {contests_activity &&
                        contests_activity.tag &&
                        contests_activity?.tag.map((tag, index) => (
                            <Chip
                                label={`${tag}`}
                                variant="outlined"
                                key={index}
                                size="small"
                                sx={{
                                    backgroundColor: theme[themeStyle].darkGray60, // Light green background
                                    color: theme[themeStyle].tag_white, // White text
                                    borderRadius: '4px', // Reduced border radius
                                    borderColor: 'transparent', // Remove border if desired
                                    fontWeight: 500, // Optional: Make text bold for better readability
                                    textTransform: 'capitalize',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                }}
                            />
                        ))}
                </Stack>

                {contests_activity && contests_activity.categories && !isEmpty(contests_activity.categories) && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ marginBottom: 2 }} px={2} my={2}>
                        <Category
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontSize: '18px',
                            }}
                            style={{
                                transform: 'scaleX(-1)',
                            }}
                        />
                        {contests_activity && contests_activity.categories && (
                            <CustomChipGroup
                                tags={contests_activity?.categories.map(cat => cat.category_name) as string[]}
                                maxVisible={2}
                            />
                        )}
                    </Stack>
                )}

                {contests_activity && contests_activity?.host && (
                    <Stack direction="row" alignItems="center" spacing={1} px={2}>
                        <Avatar
                            src={contests_activity?.host.profile_picture} // Replace with actual avatar image
                            alt={contests_activity?.host?.name}
                            sx={{ width: 32, height: 32 }}
                        />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 10,
                                    color: theme[themeStyle].tag_white,
                                }}>
                                Hosted by {contests_activity?.host.name}
                            </Typography>
                        </Box>
                    </Stack>
                )}
            </CardContent>

            {isNewContest && (
                <Stack direction={'row'} gap={'5px'} alignItems="center" px={2} py={2} my={2}>
                    <CustomButton
                        label={isWonContest ? 'See Winners' : 'Join Contest'}
                        onClick={handleShowSubmissions}
                        sx={{
                            flex: {
                                xs: 0.4,
                                sm: 0.4,
                                md: 0.4,
                                lg: 0.6,
                            },
                            '&:hover': {
                                WebkitTextFillColor: theme[themeStyle].primary_orange,
                            },
                        }}
                    />
                    {/* Interactions Section */}
                    <Stack
                        direction="row"
                        justifyContent="space-around"
                        alignItems="center"
                        p={2}
                        flex={{
                            xs: 0.6,
                            sm: 0.6,
                            md: 0.6,
                            lg: 0.4,
                        }}>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}
                                onClick={onCommentClick}>
                                <CustomCommentIcon
                                    stroke={theme[themeStyle].tag_white}
                                    height={'20px'}
                                    width={'20px'}
                                />
                            </IconButton>
                            <Typography
                                style={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 12,
                                }}
                                marginLeft={1}>
                                {formatNumberWithK(contests_activity?.total_comments!)}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                sx={{
                                    bgcolor: theme[themeStyle].deep_grey,
                                    borderRadius: '50%',
                                    '&:disabled': {
                                        bgcolor: theme[themeStyle].deep_grey,
                                        opacity: 0.7,
                                    },
                                }}
                                onClick={handleLike}
                                disabled={likingInProgress}>
                                <LoveIcon
                                    fill={
                                        contests_activity?.is_user_liked
                                            ? theme[themeStyle].secondary_red
                                            : theme[themeStyle].tag_white
                                    }
                                    filled={contests_activity?.is_user_liked}
                                    height={'20px'}
                                    width={'20px'}
                                />
                            </IconButton>
                            <Typography
                                style={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 12,
                                }}
                                marginLeft={1}>
                                {formatNumberWithK(contests_activity?.total_likes!)}
                            </Typography>
                        </Box>

                        <Box display="flex" alignItems="center">
                            <IconButton sx={{ bgcolor: theme[themeStyle].deep_grey, borderRadius: '50%' }}>
                                <SignalCellularAltIcon
                                    sx={{
                                        fontSize: '16px',
                                        color: theme[themeStyle].tag_white,
                                    }}
                                />
                            </IconButton>
                            <Typography
                                style={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontSize: 12,
                                }}
                                marginLeft={1}>
                                {formatNumberWithK(contests_activity?.trending_marker!)}
                            </Typography>
                        </Box>
                    </Stack>
                </Stack>
            )}
        </Card>
    );
};

export { FeedContestCard };
